import { createSlice } from "@reduxjs/toolkit"
import { PayloadAction } from "@reduxjs/toolkit/dist/createAction"
import { basicInitialState } from "@src/Actions/ReducerUtils"
import { tCyberConciergeState } from "@src/Model/Model"
import { ICyberConciergeAlert } from "@src/types"
import {
  CyberConciergeConfig,
  CyberConciergeMessageJson,
  WeaknessJson,
} from "@zeguro/schema-validator/dist/types/coco/cyberConcierge/combined"
export const initialCyberConciergeState: tCyberConciergeState = {
  getCyberConciergeConfig: {
    cyberConciergePage: {
      cyberConciergeIconUrl: "",
      cyberConciergeTitle: "",
      cyberConciergeTitleSubText: "",
      cyberConciergeHeading: "",
      cyberConciergeSubscriptionInformation: "",
      cyberConciergeOfferingsHeading: "",
      cyberConciergeEmailCta: {
        cyberConciergeCtaText: "",
        cyberConciergeCtaBtnInfo: "PRIMARY",
        cyberConciergeCtaIcon: "",
      },
      cyberConciergeCallCta: {
        cyberConciergeCtaText: "",
        cyberConciergeCtaBtnInfo: "PRIMARY",
        cyberConciergeCtaIcon: "",
      },
      cyberConciergeOfferings: [],
      cyberConciergeKnowledgeBase: {
        cyberConciergeKnowledgeBaseLinkText: "",
        cyberConciergeKnowledgeBaseIcon: "",
      },
    },
    ...basicInitialState,
  },
  getCyberConciergeAlert: {
    data: null,
    ...basicInitialState,
  },
  getCyberConciergeAlerts: { alerts: [], ...basicInitialState },
  getCyberConciergeWeakness: {
    data: null,
    ...basicInitialState,
  },
  getCyberConciergeWeaknesses: {
    data: [],
    ...basicInitialState,
  },
}

const cyberConciergeSlice = createSlice({
  name: "cyberConcierge",
  initialState: initialCyberConciergeState,
  reducers: {
    getCyberConciergeConfigRequest({ getCyberConciergeConfig }) {
      getCyberConciergeConfig.isLoading = true
      getCyberConciergeConfig.error = undefined
    },
    getCyberConciergeConfigSuccess(
      { getCyberConciergeConfig },
      { payload }: PayloadAction<{ data: CyberConciergeConfig }>,
    ) {
      getCyberConciergeConfig.cyberConciergePage =
        payload.data.cyberConciergePage
      getCyberConciergeConfig.isLoading = false
      getCyberConciergeConfig.success = true
      getCyberConciergeConfig.error = undefined
    },
    getCyberConciergeConfigFailure(
      { getCyberConciergeConfig },
      { payload }: PayloadAction<Error>,
    ) {
      getCyberConciergeConfig.error = payload
      getCyberConciergeConfig.isLoading = false
    },
    getCyberConciergeAlertsRequest({ getCyberConciergeAlerts }) {
      getCyberConciergeAlerts.isLoading = true
      getCyberConciergeAlerts.error = undefined
    },
    getCyberConciergeAlertsSuccess(
      { getCyberConciergeAlerts },
      { payload }: { payload: ICyberConciergeAlert[] },
    ) {
      getCyberConciergeAlerts.alerts = payload
      getCyberConciergeAlerts.isLoading = false
      getCyberConciergeAlerts.success = true
      getCyberConciergeAlerts.error = undefined
    },
    getCyberConciergeAlertsFailure(
      { getCyberConciergeAlerts },
      { payload }: PayloadAction<Error>,
    ) {
      getCyberConciergeAlerts.error = payload
      getCyberConciergeAlerts.isLoading = false
    },
    getCyberConciergeAlertRequest({ getCyberConciergeAlert }) {
      getCyberConciergeAlert.isLoading = true
      getCyberConciergeAlert.error = undefined
    },
    getCyberConciergeAlertSuccess(
      { getCyberConciergeAlert },
      { payload }: PayloadAction<{ data: CyberConciergeMessageJson }>,
    ) {
      getCyberConciergeAlert.data = payload.data
      getCyberConciergeAlert.isLoading = false
      getCyberConciergeAlert.success = true
      getCyberConciergeAlert.error = undefined
    },
    getCyberConciergeAlertFailure(
      { getCyberConciergeAlert },
      { payload }: PayloadAction<Error>,
    ) {
      getCyberConciergeAlert.error = payload
      getCyberConciergeAlert.isLoading = false
    },
    getCyberConciergeWeaknessRequest({ getCyberConciergeWeakness }) {
      getCyberConciergeWeakness.isLoading = true
      getCyberConciergeWeakness.error = undefined
    },
    getCyberConciergeWeaknessSuccess(
      { getCyberConciergeWeakness },
      { payload }: PayloadAction<{ data: WeaknessJson }>,
    ) {
      getCyberConciergeWeakness.data = payload.data
      getCyberConciergeWeakness.isLoading = false
      getCyberConciergeWeakness.success = true
      getCyberConciergeWeakness.error = undefined
    },
    getCyberConciergeWeaknessFailure(
      { getCyberConciergeWeakness },
      { payload }: PayloadAction<Error>,
    ) {
      getCyberConciergeWeakness.error = payload
      getCyberConciergeWeakness.isLoading = false
    },
    getCyberConciergeWeaknessesRequest({ getCyberConciergeWeaknesses }) {
      getCyberConciergeWeaknesses.isLoading = true
      getCyberConciergeWeaknesses.error = undefined
    },
    getCyberConciergeWeaknessesSuccess(
      { getCyberConciergeWeaknesses },
      { payload }: PayloadAction<{ data: any }>,
    ) {
      getCyberConciergeWeaknesses.data = payload.data
      getCyberConciergeWeaknesses.isLoading = false
      getCyberConciergeWeaknesses.success = true
      getCyberConciergeWeaknesses.error = undefined
    },
    getCyberConciergeWeaknessesFailure(
      { getCyberConciergeWeaknesses },
      { payload }: PayloadAction<Error>,
    ) {
      getCyberConciergeWeaknesses.error = payload
      getCyberConciergeWeaknesses.isLoading = false
    },
  },
})

export const { name, actions, reducer } = cyberConciergeSlice
